/**
 * Who is talking to this customer — the AI or a person. (E5, Ajay #5.)
 *
 * Ajay's complaint at the September showcase was that you could not tell, at a
 * glance, whether a conversation was being handled by the AI or by a consultant.
 * The data to answer it already existed on every lead; nothing displayed it.
 *
 * ── Two states, by who is actually replying ──
 * This used to have a third, "AI + Human" (a consultant assigned while the AI
 * still replies). The client asked for exactly two, and since leads now get an
 * owner automatically that third state would have appeared on nearly every
 * card. The label follows `assistedBy` alone — who answers the customer's next
 * message. WHO OWNS THE LEAD is a different question and is still shown
 * separately (the card's consultant avatar, the Assigned consultant panel).
 *
 * ── Deliberately not a traffic light (T041) ──
 * Every state carries a word. Ownership and "this needs attention" are two
 * different questions, and the showcase build answered both with colour alone,
 * so a red lead could mean either. Attention keeps the pulsing dot; ownership
 * gets a labelled chip. Colour here is reinforcement, never the message —
 * which is also what makes it survive the three themes (T042) and colour-blind
 * viewers.
 */

export const OWNERSHIP = {
  AI: {
    key: 'AI',
    label: 'AI',
    long: 'Conversation with AI',
    color: 'var(--accent)',
    tint: 'color-mix(in srgb, var(--accent) 12%, transparent)',
  },
  HUMAN: {
    key: 'HUMAN',
    label: 'Human',
    long: 'Conversation with a consultant',
    color: 'var(--success)',
    tint: 'color-mix(in srgb, var(--success) 14%, transparent)',
  },
};

/**
 * Resolve ownership from a lead.
 *
 * A consultant taking over sets `assistedBy = HUMAN` — the AI has stopped.
 * Anything else means the AI is live, whether or not a consultant is assigned.
 */
export function ownershipOf(lead) {
  if (!lead) return OWNERSHIP.AI;
  const assisted = (lead.assistedBy || 'AI').toUpperCase();
  return assisted === 'HUMAN' ? OWNERSHIP.HUMAN : OWNERSHIP.AI;
}

/** Counts for a set of leads, in the order the dashboard shows them. */
export function ownershipCounts(leads = []) {
  const out = { AI: 0, HUMAN: 0 };
  for (const l of leads) out[ownershipOf(l).key] += 1;
  return out;
}
